import { useDeviceType } from '@cian/ui-kit';
import cn from 'clsx';
import { AdvantagesBlock } from 'shared/components/AdvantagesBlock';
import { ContainerInner } from 'shared/components/ContainerInner';
import { MortgageWrapperIdName, RootMortgageWrapper } from 'shared/constants/common';
import { BankPartnersBlockContainer } from '../BankPartnersBlockContainer';
import { ControlsContainer } from '../ControlsContainer';
import { InformationalSeoBlock } from '../InformationalSeoBlock/InformationalSeoBlock';
import { ListingContainer } from '../ListingContainer';
import { ProgramsBlockContainer } from '../ProgramsBlockContainer';
import {
  FillApplicationButtonMobile,
  useFillApplicationStickyButtons,
  useMobileStickyButtons,
} from '../stickyButtons/FillApplicationButton';
import { RedesignStickyBar } from '../stickyButtons/RedesignStickyBar';

import { ErrorLogComponent } from '@cian/error-log-component';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ETestIds } from '../../constants/testIds';
import { useGetMappedOffers } from '../../hooks/useGetMappedOffers';
import { selectIsShadowMode } from '../../selectors/dictionaries/selectIsShadowMode';
import { selectCalculatorValues } from '../../selectors/landing';
import { selectIsAuthorizedModalOpen } from '../../selectors/landing/selectIsAuthorizedModalOpen';
import { ERuntimeSettings } from '../../types/runtimeSettings';
import { useApplicationContext } from '../../utils/applicationContext';
import { CreditInfoModalContainer } from '../CreditInfoModalContainer';
import { LoginWidgetMicrofrontend } from '../microfrontends/LoginWidgetMicrofrontend';
import { QuickLinks } from '../QuickLinksContainer';
import { SeoCrossLinksContainer } from '../SeoCrossLinksContainer';
import { ShortOfferTermsModalContainer } from '../ShortOfferTermsModalContainer';
import * as styles from './styles.css';

export function MainContentContainer() {
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const { config, logger } = useApplicationContext();
  const [btnRef, setBtnRef] = useState<HTMLElement | null>(null);
  const calculatorValues = useSelector(selectCalculatorValues);
  const { mappedOffers, isFetching } = useGetMappedOffers(calculatorValues);
  const isAuthorizedModalOpen = useSelector(selectIsAuthorizedModalOpen);
  /** После первого открытия держим хост виджета смонтированным, чтобы при закрытии передать `isWidgetOpen: false` и не рвать портал/оверлей без cleanup у MF. */
  const [keepLoginWidgetMounted, setKeepLoginWidgetMounted] = useState(false);
  const isSeoCrossLinksEnabled = config.get<boolean>(ERuntimeSettings.isSeoCrossLinksEnabled);
  const isWebView = config.get<boolean>(ERuntimeSettings.isWebView) ?? false;
  const showCrossLinks = isSeoCrossLinksEnabled && !isWebView;
  const isShadowMode = useSelector(selectIsShadowMode);
  const mortgageLandingRedesignEnabled = config.get<boolean>(ERuntimeSettings.mortgageLandingRedesign);
  useFillApplicationStickyButtons(mappedOffers.length);
  useMobileStickyButtons(btnRef, { enabled: !mortgageLandingRedesignEnabled });

  const handleSetFormRef = useCallback((node: HTMLFormElement | null) => {
    setBtnRef(node);
  }, []);

  useEffect(() => {
    if (isAuthorizedModalOpen) {
      setKeepLoginWidgetMounted(true);
    }
  }, [isAuthorizedModalOpen]);

  const showTopQuickLinks = showCrossLinks && !mortgageLandingRedesignEnabled;

  return (
    <>
      <div
        className={cn(
          mortgageLandingRedesignEnabled ? styles['root-redesign'] : styles['root'],
          mortgageLandingRedesignEnabled && styles[deviceType],
          showTopQuickLinks && styles['root--with-crosslinks'],
          isShadowMode && styles['root--shadow-mode'],
        )}
        id={RootMortgageWrapper}
      >
        <ContainerInner>
          {showTopQuickLinks && <QuickLinks />}
          <div
            id={MortgageWrapperIdName}
            className={cn(
              mortgageLandingRedesignEnabled ? styles['wrapper-redesign'] : styles['wrapper'],
              mortgageLandingRedesignEnabled && styles[deviceType],
            )}
            data-testid={ETestIds.RootMortgageWrapper}
          >
            <ControlsContainer offersCount={mappedOffers.length} offersIsFetching={isFetching} />
            <ListingContainer mappedOffersList={mappedOffers} offersIsFetching={isFetching} />
          </div>
        </ContainerInner>
        {mortgageLandingRedesignEnabled && (
          <>
            <ContainerInner>
              <AdvantagesBlock />
            </ContainerInner>
            <ProgramsBlockContainer />
            <ContainerInner>
              <BankPartnersBlockContainer />
            </ContainerInner>
          </>
        )}
        <ContainerInner>
          <InformationalSeoBlock offersLength={mappedOffers.length} />
        </ContainerInner>
        {showCrossLinks && mortgageLandingRedesignEnabled && (
          <ErrorLogComponent logger={logger}>
            <SeoCrossLinksContainer variant="redesign" />
          </ErrorLogComponent>
        )}
        {!isDesktop &&
          (mortgageLandingRedesignEnabled ? (
            <RedesignStickyBar mappedOffers={mappedOffers} offersIsFetching={isFetching} />
          ) : (
            <div className={styles['overlay-background']}>
              <FillApplicationButtonMobile handleOnload={handleSetFormRef} />
            </div>
          ))}
      </div>
      {showCrossLinks && !mortgageLandingRedesignEnabled && (
        <ErrorLogComponent logger={logger}>
          <SeoCrossLinksContainer variant="default" />
        </ErrorLogComponent>
      )}
      <CreditInfoModalContainer />
      {mortgageLandingRedesignEnabled && <ShortOfferTermsModalContainer />}
      {keepLoginWidgetMounted && <LoginWidgetMicrofrontend />}
    </>
  );
}
